import { FontService } from '../../types';
import type { TextElementFont } from '../../types';

export type SelectedFontMetrics = {
  upm: number;
  asc: number;
  des: number;
  lineGap: number;
  capHeight: number;
  source: 'regular' | 'typo';
};

export type FontMetricOverrides = {
  ascentOverride: string;
  descentOverride: string;
  lineGapOverride: string;
};

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const toPercentage = (value: number, upm: number) =>
  `${Number(((value / upm) * 100).toFixed(4))}%`;

/** Selects the same custom-font metric policy used by Page Generator. */
export function selectFontMetrics(
  font: TextElementFont
): SelectedFontMetrics | null {
  const { metrics } = font;
  if (
    !metrics ||
    !isFiniteNumber(metrics.upm) ||
    metrics.upm === 0 ||
    !isFiniteNumber(metrics.asc) ||
    !isFiniteNumber(metrics.des)
  ) {
    return null;
  }

  const capHeight = isFiniteNumber(metrics.capH) ? metrics.capH : metrics.asc;
  if (
    font.service === FontService.Custom &&
    font.useTypoMetrics === true &&
    isFiniteNumber(metrics.tAsc) &&
    isFiniteNumber(metrics.tDes)
  ) {
    return {
      upm: metrics.upm,
      asc: metrics.tAsc,
      des: metrics.tDes,
      lineGap: isFiniteNumber(metrics.tLGap) ? metrics.tLGap : 0,
      capHeight,
      source: 'typo',
    };
  }

  return {
    upm: metrics.upm,
    asc: metrics.asc,
    des: metrics.des,
    lineGap: isFiniteNumber(metrics.lGap) ? metrics.lGap : 0,
    capHeight,
    source: 'regular',
  };
}

/** Keeps the browser's custom-font line box aligned with our geometry model. */
export function getFontMetricOverrides(
  font: TextElementFont
): FontMetricOverrides | null {
  if (
    font.service !== FontService.Custom ||
    font.useTypoMetrics === undefined
  ) {
    return null;
  }

  const metrics = selectFontMetrics(font);
  if (!metrics) {
    return null;
  }

  return {
    ascentOverride: toPercentage(metrics.asc, metrics.upm),
    descentOverride: toPercentage(Math.abs(metrics.des), metrics.upm),
    lineGapOverride: toPercentage(Math.max(0, metrics.lineGap), metrics.upm),
  };
}
